import { Link } from 'react-router-dom';
import {
  Flame, Loader2, Lock, ChevronLeft, ChevronRight, Star, Shuffle,
  ShoppingCart, Pencil,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { FavoriteMealsSection } from '../nutrition/FavoriteMealsSection';

const dietLabels = {
  vegetarian: 'Vegetarian', vegan: 'Vegan', pescatarian: 'Pescatarian',
  gluten_free: 'Gluten-Free', dairy_free: 'Dairy-Free', nut_free: 'Nut Allergy',
  keto: 'Keto', halal: 'Halal', kosher: 'Kosher',
};

const mealSignature = (foods) => (foods || '').trim().toLowerCase().slice(0, 200);

const NoPlanCTA = ({ user, premiumLocked, generating, onGenerate, onUnlock }) => (
  <div className="text-center py-4">
    <p className="text-muted-foreground text-sm mb-3">
      Get a personalized meal plan with macros tailored to your goals
    </p>
    {(user?.dietary_restrictions?.length > 0 || user?.custom_dietary_notes) ? (
      <div className="mb-4 p-3 bg-green-500/5 border border-green-500/20 rounded text-left">
        <p className="text-xs text-green-400 font-heading uppercase tracking-wider mb-2">
          Your AI plan will respect:
        </p>
        <div className="flex flex-wrap gap-1.5">
          {(user?.dietary_restrictions || []).map(r => (
            <Badge key={r} variant="outline" className="border-green-500/40 text-green-300 text-xs">
              {dietLabels[r] || r}
            </Badge>
          ))}
          {user?.custom_dietary_notes && (
            <Badge variant="outline" className="border-green-500/40 text-green-300 text-xs">
              {user.custom_dietary_notes}
            </Badge>
          )}
        </div>
        <Link to="/profile" className="text-xs text-muted-foreground hover:text-green-400 underline mt-2 inline-block">
          Edit restrictions in profile →
        </Link>
      </div>
    ) : (
      <p className="text-xs text-muted-foreground mb-4">
        Have allergies or a specific diet?{' '}
        <Link to="/profile" className="text-green-400 underline">Add them in your profile</Link>
        {' '}— they'll be applied to every meal.
      </p>
    )}
    <Button
      onClick={premiumLocked ? onUnlock : onGenerate}
      disabled={!premiumLocked && generating}
      className={premiumLocked
        ? 'bg-yellow-500 text-black w-full hover:bg-yellow-400 font-heading tracking-wider uppercase'
        : 'bg-green-600 text-white w-full hover:bg-green-700'}
      data-testid="generate-nutrition-btn"
    >
      {premiumLocked ? <><Lock className="h-4 w-4 mr-2" /> Unlock With Premium</>
        : generating ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Generating Meal Plan...</>
        : <><Flame className="h-4 w-4 mr-2" /> Generate Nutrition Plan</>}
    </Button>
  </div>
);

const MealCard = ({ meal, i, fav, favoriting, onToggleFavorite, onOpenSwap, onOpenRawText }) => {
  const hasRaw = typeof meal.raw_text === 'string' && meal.raw_text.trim().length > 0;
  return (
    <div className="bg-card border border-border p-3 rounded" data-testid={`meal-card-${i}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="font-heading text-sm text-white uppercase">
          {meal.meal}
          {meal.swapped && (
            <span className="ml-2 text-[10px] text-green-400 font-normal normal-case">· swapped</span>
          )}
          {hasRaw && (
            <span className="ml-2 text-[10px] text-primary font-normal normal-case">· custom</span>
          )}
        </span>
        <span className="text-muted-foreground text-xs">{meal.time}</span>
      </div>

      {hasRaw ? (
        <p
          className="text-white text-sm mb-2 whitespace-pre-wrap"
          data-testid={`meal-raw-text-${i}`}
        >
          {meal.raw_text}
        </p>
      ) : (
        <p className="text-white text-sm mb-2">{meal.foods}</p>
      )}

      <div className="flex items-center justify-between gap-2">
        {hasRaw ? (
          <span className="text-[11px] text-muted-foreground italic">
            Macros hidden — edit to restore the AI version.
          </span>
        ) : (
          <div className="flex gap-3 text-xs flex-wrap">
            <span className="text-muted-foreground">{meal.calories} cal</span>
            <span className="text-red-400">{meal.protein_g}g P</span>
            <span className="text-yellow-400">{meal.carbs_g}g C</span>
            <span className="text-blue-400">{meal.fats_g}g F</span>
          </div>
        )}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            type="button"
            onClick={() => onToggleFavorite(meal)}
            disabled={favoriting}
            className={`flex items-center justify-center p-1.5 rounded border transition-colors ${
              fav
                ? 'text-yellow-400 border-yellow-500/40 hover:border-yellow-500/60'
                : 'text-muted-foreground hover:text-yellow-400 border-border hover:border-yellow-500/40'
            }`}
            data-testid={`favorite-meal-btn-${i}`}
            title={fav ? 'Remove from favorites' : 'Save to favorites'}
          >
            {favoriting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Star className={`h-3 w-3 ${fav ? 'fill-current' : ''}`} />}
          </button>
          <button
            type="button"
            onClick={() => onOpenRawText({
              mealIndex: i,
              initialText: meal.raw_text || '',
              mealLabel: meal.meal,
            })}
            className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 border border-primary/30 hover:border-primary/60 px-2 py-1 rounded transition-colors"
            data-testid={`edit-meal-text-btn-${i}`}
            title="Write your own description for this meal"
          >
            <Pencil className="h-3 w-3" /> Edit
          </button>
          <button
            type="button"
            onClick={() => onOpenSwap({ meal, mealIndex: i })}
            className="flex items-center gap-1 text-xs text-green-400 hover:text-green-300 border border-green-500/30 hover:border-green-500/60 px-2 py-1 rounded transition-colors"
            data-testid={`swap-meal-btn-${i}`}
            title="Swap this meal with AI alternatives"
          >
            <Shuffle className="h-3 w-3" /> Swap
          </button>
        </div>
      </div>
    </div>
  );
};

const GroceryListPanel = ({
  groceryList, nutritionPlan, groceryWeeks, setGroceryWeeks,
  generatingGrocery, onGenerate, onClear,
}) => (
  <div className="mt-4 pt-4 border-t border-border">
    <div className="flex items-center gap-2 mb-3">
      <ShoppingCart className="h-4 w-4 text-yellow-400" />
      <span className="font-heading text-sm text-white uppercase tracking-wider">Grocery List</span>
    </div>

    {!groceryList ? (
      <div>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-muted-foreground text-sm">Shop for:</span>
          <select
            value={groceryWeeks}
            onChange={(e) => setGroceryWeeks(parseInt(e.target.value, 10))}
            className="bg-black text-white text-sm px-2 py-1 border border-border rounded"
            data-testid="grocery-weeks-select"
          >
            {Array.from({ length: nutritionPlan?.duration_weeks || 8 }, (_, i) => (
              <option key={i + 1} value={i + 1}>{i + 1} week{i > 0 ? 's' : ''}</option>
            ))}
          </select>
        </div>
        <Button
          onClick={onGenerate}
          disabled={generatingGrocery}
          className="w-full bg-yellow-600 text-white hover:bg-yellow-700"
          data-testid="generate-grocery-btn"
        >
          {generatingGrocery ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Building List...</>
            : <><ShoppingCart className="h-4 w-4 mr-2" /> Generate Grocery List</>}
        </Button>
      </div>
    ) : (
      <div>
        <div className="flex items-center justify-between mb-3">
          <span className="text-muted-foreground text-xs">
            {groceryList.weeks_covered} week{groceryList.weeks_covered > 1 ? 's' : ''}
          </span>
          <span className="text-yellow-400 text-xs font-heading">
            Est. ${groceryList.estimated_cost_low}-${groceryList.estimated_cost_high}
          </span>
        </div>
        {groceryList.categories?.map((cat, ci) => (
          <div key={ci} className="mb-3">
            <p className="font-heading text-xs text-primary uppercase tracking-wider mb-1">{cat.name}</p>
            <div className="space-y-1">
              {cat.items?.map((item, ii) => (
                <div key={ii} className="flex items-center justify-between text-sm px-2 py-1 bg-card/50 rounded">
                  <span className="text-white">{item.item}</span>
                  <span className="text-muted-foreground text-xs">{item.quantity}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
        <Button
          onClick={onClear}
          variant="outline"
          className="w-full mt-2 border-border text-muted-foreground hover:text-white text-xs"
        >
          Regenerate List
        </Button>
      </div>
    )}
  </div>
);

export const NutritionPlanSection = ({
  user, nutritionPlan, generatingNutrition, premiumLocked,
  nutritionWeek, setNutritionWeek,
  favorites, favoritingMeal, removingFavorite,
  groceryList, groceryWeeks, setGroceryWeeks, generatingGrocery,
  onGenerate, onRegenerate, onUnlockPremium,
  onToggleFavorite, onRemoveFavorite, onOpenSwap, onOpenRawText,
  onGenerateGrocery, onClearGrocery,
}) => {
  const isMealFavorited = (foods) => favorites.some((f) => f.signature === mealSignature(foods));
  const weekData = nutritionPlan?.weeks?.[nutritionWeek - 1];
  return (
    <div className="mt-6 pt-6 border-t border-border">
      <div className="flex items-center gap-2 mb-4">
        <Flame className="h-5 w-5 text-green-400" />
        <h3 className="font-heading text-lg text-white uppercase tracking-wider">Your Nutrition Plan</h3>
      </div>

      {!nutritionPlan ? (
        <NoPlanCTA
          user={user}
          premiumLocked={premiumLocked}
          generating={generatingNutrition}
          onGenerate={onGenerate}
          onUnlock={onUnlockPremium}
        />
      ) : (
        <div>
          {(nutritionPlan.dietary_restrictions?.length > 0 || nutritionPlan.custom_dietary_notes) && (
            <div className="mb-3 flex flex-wrap gap-1.5">
              {(nutritionPlan.dietary_restrictions || []).map(r => (
                <Badge key={r} variant="outline" className="border-green-500/40 text-green-300 text-xs">
                  {dietLabels[r] || r}
                </Badge>
              ))}
              {nutritionPlan.custom_dietary_notes && (
                <Badge variant="outline" className="border-green-500/40 text-green-300 text-xs">
                  {nutritionPlan.custom_dietary_notes}
                </Badge>
              )}
            </div>
          )}

          {nutritionPlan.overview && (
            <div className="bg-green-500/10 border border-green-500/20 rounded p-3 mb-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-green-400 text-xs font-heading uppercase">Daily Target</span>
                <span className="text-white text-sm font-heading">
                  {nutritionPlan.overview.daily_calories_start}-{nutritionPlan.overview.daily_calories_end} cal
                </span>
              </div>
              <p className="text-muted-foreground text-xs">{nutritionPlan.overview.strategy}</p>
              <p className="text-muted-foreground text-xs mt-1">{nutritionPlan.overview.meals_per_day} meals/day</p>
            </div>
          )}

          <div className="flex items-center justify-between mb-3">
            <button onClick={() => setNutritionWeek(w => Math.max(1, w - 1))} disabled={nutritionWeek <= 1} className="text-white disabled:text-muted-foreground p-1">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <span className="font-heading text-green-400 uppercase tracking-wider text-sm">
              Week {nutritionWeek} — {weekData?.focus || ''}
            </span>
            <button onClick={() => setNutritionWeek(w => Math.min(nutritionPlan.duration_weeks || 8, w + 1))} disabled={nutritionWeek >= (nutritionPlan.duration_weeks || 8)} className="text-white disabled:text-muted-foreground p-1">
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>

          {weekData?.daily_targets && (
            <div className="grid grid-cols-4 gap-2 mb-4">
              {[
                { label: 'Cal', value: weekData.daily_targets.calories, color: 'text-white' },
                { label: 'Protein', value: `${weekData.daily_targets.protein_g}g`, color: 'text-red-400' },
                { label: 'Carbs', value: `${weekData.daily_targets.carbs_g}g`, color: 'text-yellow-400' },
                { label: 'Fats', value: `${weekData.daily_targets.fats_g}g`, color: 'text-blue-400' },
              ].map(({ label, value, color }) => (
                <div key={label} className="bg-card border border-border p-2 text-center rounded">
                  <p className={`font-heading text-sm ${color}`}>{value}</p>
                  <p className="text-muted-foreground text-xs">{label}</p>
                </div>
              ))}
            </div>
          )}

          <div className="space-y-2">
            {weekData?.sample_day?.map((meal, i) => (
              <MealCard
                key={i}
                meal={meal}
                i={i}
                fav={isMealFavorited(meal.foods)}
                favoriting={favoritingMeal === mealSignature(meal.foods)}
                onToggleFavorite={onToggleFavorite}
                onOpenSwap={onOpenSwap}
                onOpenRawText={({ mealIndex, initialText, mealLabel }) =>
                  onOpenRawText?.({ weekIndex: nutritionWeek - 1, mealIndex, initialText, mealLabel })
                }
              />
            ))}
          </div>

          <FavoriteMealsSection
            favorites={favorites}
            onRemove={onRemoveFavorite}
            removing={removingFavorite}
          />

          <Button
            onClick={onRegenerate}
            disabled={generatingNutrition}
            variant="outline"
            className="w-full mt-4 border-border text-muted-foreground hover:text-white"
            data-testid="regenerate-nutrition-btn"
          >
            {generatingNutrition ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Regenerating...</> : 'Regenerate Nutrition Plan'}
          </Button>

          <GroceryListPanel
            groceryList={groceryList}
            nutritionPlan={nutritionPlan}
            groceryWeeks={groceryWeeks}
            setGroceryWeeks={setGroceryWeeks}
            generatingGrocery={generatingGrocery}
            onGenerate={onGenerateGrocery}
            onClear={onClearGrocery}
          />
        </div>
      )}
    </div>
  );
};
